import { ref, reactive, computed } from 'vue'
import type {
	DeliberationCondition,
	DeliberationConditionRequest,
	DeliberationConditionFilters,
	Deliberation,
	DeliberationRequest,
	DeliberationFilters,
	DeliberationEtudiant,
	Predeliberation,
	PredeliberationFilters,
	PredeliberationEtudiant,
	ValiderEtudiantRequest,
	ModifierNoteRequest,
	ValiderBulkRequest,
	ValiderAvecPhaseRequest,
	CreerSecondeDeliberationRequest,
	ListeFormateeEtudiant,
	DeliberationStatistiquesPayload,
	DeliberationStatistiquesPhaseFilter,
	EtudiantRattrapage,
	NoteEliminatoire,
	Bulletin,
	DeliberationWorkflowStatus
} from '@/types/deliberation'
import type { ApiResponse } from '@/types/api'
import { useApi } from '@/composables/useApi'
import { formatErrorMessage, extractErrorDetails } from '@/utils/errorHandler'
import { applyHistoriqueParams } from '@/utils/historiqueParams'

interface DeliberationState {
	loading: boolean
	errors: Record<string, string[]>
}

export function useDeliberation() {
	const { apiData } = useApi()
	const state = reactive<DeliberationState>({
		loading: false,
		errors: {}
	})

	// ============================================================================
	// CONDITIONS DE DÉLIBÉRATION
	// ============================================================================

	async function fetchDeliberationConditions(filters?: DeliberationConditionFilters) {
		state.loading = true
		try {
			const qs = new URLSearchParams()
			if (filters?.is_active !== undefined) qs.append('is_active', String(filters.is_active))
			if (filters?.search) qs.append('search', filters.search)
			if (filters?.page) qs.append('page', String(filters.page))
			if (filters?.per_page) qs.append('per_page', String(filters.per_page))
			if (filters?.all) qs.append('all', 'true')

			const res = await apiData<ApiResponse<DeliberationCondition[] | any>>(`/deliberation-conditions?${qs.toString()}`)
			if (res?.success) {
				return { success: true, data: res.data }
			}
			return { success: false, message: res.message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la récupération des conditions de délibération',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'conditions' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function getDeliberationCondition(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<DeliberationCondition>>(`/deliberation-conditions/${id}`)
			if (res?.success) {
				return { success: true, data: res.data }
			}
			return { success: false, message: res.message }
		} catch (error: any) {
			// Ne pas afficher de toast ici, laisser la page gérer
			const message = formatErrorMessage(
				'Erreur lors de la récupération de la condition',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'condition' }
			)
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	async function createDeliberationCondition(payload: DeliberationConditionRequest) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<DeliberationCondition>>('/deliberation-conditions', {
				method: 'POST',
				body: payload
			})
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, data: res.data, message: res.message || 'Condition créée avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la création de la condition',
				res,
				{ action: 'Création', entity: 'condition' }
			)
			return { success: false, message, errors: res.errors }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la création de la condition',
				error?.response?._data || error,
				{ action: 'Création', entity: 'condition' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	async function updateDeliberationCondition(id: number, payload: Partial<DeliberationConditionRequest>) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<DeliberationCondition>>(`/deliberation-conditions/${id}`, {
				method: 'PUT',
				body: payload
			})
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, data: res.data, message: res.message || 'Condition mise à jour avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la mise à jour de la condition',
				res,
				{ action: 'Mise à jour', entity: 'condition' }
			)
			return { success: false, message, errors: res.errors }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la mise à jour de la condition de délibération',
				error?.response?._data || error,
				{ action: 'Mise à jour', entity: 'condition' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data?.errors || error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function deleteDeliberationCondition(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<null>>(`/deliberation-conditions/${id}`, {
				method: 'DELETE'
			})
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, message: res.message || 'Condition supprimée avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la suppression de la condition',
				res,
				{ action: 'Suppression', entity: 'condition' }
			)
			return { success: false, message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la suppression de la condition',
				error?.response?._data || error,
				{ action: 'Suppression', entity: 'condition' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	async function toggleActiveDeliberationCondition(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<DeliberationCondition>>(`/deliberation-conditions/${id}/toggle-active`, {
				method: 'PATCH'
			})
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, data: res.data, message: res.message || 'État modifié avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la modification de l\'état',
				res,
				{ action: 'Modification', entity: 'condition' }
			)
			return { success: false, message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la modification de l\'état',
				error?.response?._data || error,
				{ action: 'Modification', entity: 'condition' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	// ============================================================================
	// DÉLIBÉRATIONS
	// ============================================================================

	async function fetchDeliberations(filters?: DeliberationFilters) {
		state.loading = true
		try {
			const qs = new URLSearchParams()
			if (filters?.session_id) qs.append('session_id', String(filters.session_id))
			if (filters?.specialite_id) qs.append('specialite_id', String(filters.specialite_id))
			if (filters?.semestre_id) qs.append('semestre_id', String(filters.semestre_id))
			if (filters?.type_deliberation) qs.append('type_deliberation', filters.type_deliberation)
			if (filters?.etat) qs.append('etat', filters.etat)
			if (filters?.responsable_id) qs.append('responsable_id', String(filters.responsable_id))
			if (filters?.page) qs.append('page', String(filters.page))
			if (filters?.per_page) qs.append('per_page', String(filters.per_page))
			if (filters?.all) qs.append('all', 'true')
			applyHistoriqueParams(qs, {
				includeHistorique: filters?.includeHistorique,
				anneeUniversitaire: filters?.anneeUniversitaire,
			})

			const url = `/deliberations${qs.toString() ? `?${qs.toString()}` : ''}`
			const res = await apiData<ApiResponse<Deliberation[] | any>>(url)
			if (res?.success) {
				return { success: true, data: res.data }
			}
			return { success: false, message: res.message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la récupération des délibérations',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'délibérations' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data?.errors || error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function getDeliberation(id: number) {
		state.loading = true
		try {
			// Inclure les relations deliberation_condition et predeliberation
			const res = await apiData<ApiResponse<Deliberation>>(`/deliberations/${id}?with=deliberation_condition,predeliberation`)
			if (res?.success) {
				return { success: true, data: res.data }
			}
			return { success: false, message: res.message }
		} catch (error: any) {
			// Ne pas afficher de toast ici, laisser la page gérer
			const message = formatErrorMessage(
				'Erreur lors de la récupération de la délibération',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'délibération' }
			)
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	async function createDeliberationWithPredeliberation(payload: DeliberationRequest) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<{ deliberation: Deliberation; predeliberation: Predeliberation }>>(
				'/deliberations/creer-avec-predeliberation',
				{
					method: 'POST',
					body: payload
				}
			)
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, data: res.data, message: res.message || 'Délibération et prédélibération créées avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la création de la délibération et prédélibération',
				res,
				{ action: 'Création', entity: 'délibération' }
			)
			return { success: false, message, errors: res.errors }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la création de la délibération et prédélibération',
				error?.response?._data || error,
				{ action: 'Création', entity: 'délibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data?.errors || error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function demarrerPhase1(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<Deliberation>>(`/deliberations/${id}/demarrer-phase-1`, {
				method: 'POST'
			})
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, data: res.data, message: res.message || 'Phase 1 démarrée avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors du démarrage de la phase 1',
				res,
				{ action: 'Démarrage Phase 1', entity: 'délibération' }
			)
			return { success: false, message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors du démarrage de la Phase 1',
				error?.response?._data || error,
				{ action: 'Démarrage Phase 1', entity: 'délibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function terminerPhase1(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<Deliberation> & { statistiques_snapshot?: DeliberationStatistiquesPayload }>(
				`/deliberations/${id}/terminer-phase-1`,
				{ method: 'POST' }
			)
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				const snapshot = (res as any)?.statistiques_snapshot as DeliberationStatistiquesPayload | undefined
				return {
					success: true,
					data: res.data,
					message: res.message || 'Phase 1 terminée avec succès',
					statistiques_snapshot: snapshot
				}
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la fin de la phase 1',
				res,
				{ action: 'Fin Phase 1', entity: 'délibération' }
			)
			return { success: false, message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la fin de la Phase 1',
				error?.response?._data || error,
				{ action: 'Fin Phase 1', entity: 'délibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function demarrerPhase2(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<Deliberation>>(`/deliberations/${id}/demarrer-phase-2`, {
				method: 'POST'
			})
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, data: res.data, message: res.message || 'Phase 2 démarrée avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors du démarrage de la phase 2',
				res,
				{ action: 'Démarrage Phase 2', entity: 'délibération' }
			)
			return { success: false, message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors du démarrage de la Phase 2',
				error?.response?._data || error,
				{ action: 'Démarrage Phase 2', entity: 'délibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function terminerPhase2(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<Deliberation> & { statistiques_snapshot?: DeliberationStatistiquesPayload }>(
				`/deliberations/${id}/terminer-phase-2`,
				{ method: 'POST' }
			)
			if (res?.success) {
				const snapshot = (res as any)?.statistiques_snapshot as DeliberationStatistiquesPayload | undefined
				return {
					success: true,
					data: res.data,
					message: res.message || 'Phase 2 terminée avec succès',
					statistiques_snapshot: snapshot
				}
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la fin de la phase 2',
				res,
				{ action: 'Fin Phase 2', entity: 'délibération' }
			)
			return { success: false, message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la fin de la Phase 2',
				error?.response?._data || error,
				{ action: 'Fin Phase 2', entity: 'délibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function validerDeliberation(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<Deliberation> & { statistiques_snapshot?: DeliberationStatistiquesPayload }>(
				`/deliberations/${id}/valider`,
				{ method: 'POST' }
			)
			if (res?.success) {
				const snapshot = (res as any)?.statistiques_snapshot as DeliberationStatistiquesPayload | undefined
				return {
					success: true,
					data: res.data,
					message: res.message || 'Délibération validée avec succès',
					statistiques_snapshot: snapshot
				}
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la validation de la délibération',
				res,
				{ action: 'Validation', entity: 'délibération' }
			)
			return { success: false, message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la validation de la délibération',
				error?.response?._data || error,
				{ action: 'Validation', entity: 'délibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function annulerDeliberation(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<Deliberation>>(`/deliberations/${id}/annuler`, {
				method: 'POST'
			})
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, data: res.data, message: res.message || 'Délibération annulée avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de l\'annulation de la délibération',
				res,
				{ action: 'Annulation', entity: 'délibération' }
			)
			return { success: false, message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de l\'annulation de la délibération',
				error?.response?._data || error,
				{ action: 'Annulation', entity: 'délibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	/**
	 * Statistiques agrégées (carte, modals, accordéon). N’utilise pas `state.loading` global pour permettre un refresh en arrière-plan.
	 * ETag renvoyé pour polling futur (`If-None-Match`).
	 */
	async function getDeliberationStatistiques(
		id: number,
		options?: {
			phase?: DeliberationStatistiquesPhaseFilter
			ifNoneMatch?: string | null
		}
	): Promise<{
		success: boolean
		data?: DeliberationStatistiquesPayload
		message?: string
		notModified?: boolean
		etag?: string
	}> {
		const phase: DeliberationStatistiquesPhaseFilter = options?.phase ?? 'all'
		const qs = new URLSearchParams()
		qs.append('phase', phase)
		let capturedEtag: string | undefined
		const fetchOpts: Record<string, unknown> = {
			method: 'GET',
			onResponse({ response }: { response: Response }) {
				const e = response.headers.get('etag')
				if (e) capturedEtag = e
			}
		}
		if (options?.ifNoneMatch) {
			fetchOpts.headers = {
				'If-None-Match': options.ifNoneMatch
			}
		}
		try {
			const res = await apiData<ApiResponse<DeliberationStatistiquesPayload>>(
				`/deliberations/${id}/statistiques?${qs.toString()}`,
				fetchOpts as any
			)
			if (res && res.success && res.data) {
				return { success: true, data: res.data as DeliberationStatistiquesPayload, message: res.message, etag: capturedEtag }
			}
			return {
				success: false,
				message: res?.message || 'Statistiques indisponibles'
			}
		} catch (error: any) {
			const status = error?.response?.status ?? error?.statusCode ?? error?.status
			if (status === 304) {
				return { success: true, notModified: true, etag: capturedEtag || options?.ifNoneMatch || undefined }
			}
			const message = formatErrorMessage(
				'Erreur lors de la récupération des statistiques',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'statistiques délibération' }
			)
			return { success: false, message }
		}
	}

	async function getListeFormatee(id: number, phase?: 'phase_1' | 'phase_2', type: 'deliberation' | 'predeliberation' = 'deliberation') {
		state.loading = true
		try {
			const qs = new URLSearchParams()
			if (phase) qs.append('phase', phase)
			qs.append('type', type)

			const res = await apiData<ApiResponse<ListeFormateeEtudiant[]>>(`/deliberations/${id}/liste-formatee?${qs.toString()}`)
			if (res?.success) {
				return { success: true, data: res.data }
			}
			return { success: false, message: res.message }
		} catch (error: any) {
			// Ne pas afficher de toast ici, laisser la page gérer
			const message = formatErrorMessage(
				'Erreur lors de la récupération de la liste',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'liste étudiants' }
			)
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	/** Une seule ligne "liste formatée" pour rechargement ciblé (GET /deliberations/{id}/etudiant). Réponse : data = objet unique, pas tableau. */
	async function getDeliberationEtudiantListeFormatee(deliberationId: number, inscriptionId: number, phase: 'phase_1' | 'phase_2') {
		state.loading = true
		try {
			const qs = new URLSearchParams()
			qs.append('inscription_id', String(inscriptionId))
			qs.append('phase', phase)
			const res = await apiData<ApiResponse<ListeFormateeEtudiant>>(`/deliberations/${deliberationId}/etudiant?${qs.toString()}`)
			// Backend retourne un objet unique dans data, pas un tableau
			if (res?.success && res.data != null && !Array.isArray(res.data)) {
				return { success: true, data: res.data }
			}
			return { success: false, message: res.message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la récupération de l\'étudiant (liste formatée)',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'étudiant délibération' }
			)
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	async function getEtudiantsRattrapage(
		id: number,
		options?: { inscription_id?: number; source?: 'outcome' | 'premiere' }
	) {
		state.loading = true
		try {
			const qs = new URLSearchParams()
			if (options?.inscription_id != null) qs.append('inscription_id', String(options.inscription_id))
			if (options?.source) qs.append('source', options.source)
			const url = `/deliberations/${id}/etudiants-rattrapage${qs.toString() ? `?${qs.toString()}` : ''}`
			const res = await apiData<ApiResponse<EtudiantRattrapage[]>>(url)
			if (res?.success) {
				return { success: true, data: res.data }
			}
			return { success: false, message: res.message }
		} catch (error: any) {
			// Ne pas afficher de toast ici, laisser la page gérer
			const message = formatErrorMessage(
				'Erreur lors de la récupération des étudiants en rattrapage',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'étudiants rattrapage' }
			)
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	/** Issue de la délibération {id} — feuille de rattrapage, liste « à l'issue », export PDF. */
	function getRattrapageDeliberationOutcome(id: number, options?: { inscription_id?: number }) {
		return getEtudiantsRattrapage(id, { ...options, source: 'outcome' })
	}

	/** Source cohorte rattrapage pour le workflow 2e délib. Passer l'ID de la 2e délib. (ou 1re en 1re session) : le backend résout la 1re en interne. */
	function getRattrapageSourcePremiere(deliberationIdForRattrapage: number, options?: { inscription_id?: number }) {
		return getEtudiantsRattrapage(deliberationIdForRattrapage, { ...options, source: 'premiere' })
	}

	async function getNotesEliminatoires(id: number, inscriptionId: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<NoteEliminatoire[]>>(`/deliberations/${id}/notes-eliminatoires`, {
				method: 'POST',
				body: { inscription_id: inscriptionId }
			})
			if (res?.success) {
				return { success: true, data: res.data }
			}
			return { success: false, message: res.message }
		} catch (error: any) {
			// Ne pas afficher de toast ici, laisser la page gérer
			const message = formatErrorMessage(
				'Erreur lors de la récupération des notes éliminatoires',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'notes éliminatoires' }
			)
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	async function creerSecondeDeliberation(id: number, payload: CreerSecondeDeliberationRequest) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<{ deliberation: Deliberation; predeliberation: Predeliberation; nombre_etudiants: number }>>(
				`/deliberations/${id}/creer-seconde-deliberation`,
				{
					method: 'POST',
					body: payload
				}
			)
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, data: res.data, message: res.message || 'Seconde délibération créée avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la création de la seconde délibération',
				res,
				{ action: 'Création', entity: 'seconde délibération' }
			)
			return { success: false, message, errors: res.errors }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la création de la seconde délibération',
				error?.response?._data || error,
				{ action: 'Création', entity: 'seconde délibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	// ============================================================================
	// PRÉDÉLIBÉRATIONS
	// ============================================================================

	async function fetchPredeliberations(filters?: PredeliberationFilters) {
		state.loading = true
		try {
			const qs = new URLSearchParams()
			if (filters?.session_id) qs.append('session_id', String(filters.session_id))
			if (filters?.specialite_id) qs.append('specialite_id', String(filters.specialite_id))
			if (filters?.semestre_id) qs.append('semestre_id', String(filters.semestre_id))
			if (filters?.etat) qs.append('etat', filters.etat)
			if (filters?.responsable_id) qs.append('responsable_id', String(filters.responsable_id))
			if (filters?.page) qs.append('page', String(filters.page))
			if (filters?.per_page) qs.append('per_page', String(filters.per_page))
			if (filters?.all) qs.append('all', 'true')
			applyHistoriqueParams(qs, {
				includeHistorique: filters?.includeHistorique,
				anneeUniversitaire: filters?.anneeUniversitaire,
			})

			const url = `/predeliberations${qs.toString() ? `?${qs.toString()}` : ''}`
			const res = await apiData<ApiResponse<Predeliberation[] | any>>(url)
			if (res?.success) {
				return { success: true, data: res.data }
			}
			return { success: false, message: res.message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la récupération des prédélibérations',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'prédélibérations' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data?.errors || error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function getPredeliberation(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<Predeliberation>>(`/predeliberations/${id}`)
			if (res?.success) {
				return { success: true, data: res.data }
			}
			return { success: false, message: res.message }
		} catch (error: any) {
			// Ne pas afficher de toast ici, laisser la page gérer
			const message = formatErrorMessage(
				'Erreur lors de la récupération de la prédélibération',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'prédélibération' }
			)
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	async function getDeliberationEtudiants(id: number, phase?: 'phase_1' | 'phase_2', statut?: string, options?: { inscription_id?: number }) {
		state.loading = true
		try {
			const qs = new URLSearchParams()
			if (phase) qs.append('phase', phase)
			if (statut) qs.append('statut', statut)
			if (options?.inscription_id != null) qs.append('inscription_id', String(options.inscription_id))
			
			const url = `/deliberations/${id}/etudiants${qs.toString() ? `?${qs.toString()}` : ''}`
			const res = await apiData<ApiResponse<DeliberationEtudiant[]>>(url)
			if (res?.success) {
				return { success: true, data: res.data || [] }
			}
			return { success: false, message: res.message, data: [] }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la récupération des étudiants de la délibération',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'étudiants délibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, data: [], errors: extractErrorDetails(error?.response?._data?.errors || error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function getListeFormateePredeliberation(id: number) {
		state.loading = true
		try {
		const res = await apiData<ApiResponse<ListeFormateeEtudiant[]>>(`/predeliberations/${id}/liste-formatee`)
		if (res?.success) {
			// S'assurer que res.data est un tableau
			const data = Array.isArray(res.data) ? res.data : []
			return { success: true, data }
		}
			return { success: false, message: res.message, data: [] }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la récupération de la liste formatée',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'liste formatée' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, data: [], errors: extractErrorDetails(error?.response?._data?.errors || error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function validerEtudiantPredeliberation(id: number, payload: ValiderEtudiantRequest) {
		state.loading = true
		try {
			// Le backend retourne: { predeliberation_etudiant: PredeliberationEtudiant, tous_etudiants_valides: boolean }
			const res = await apiData<ApiResponse<{ predeliberation_etudiant: PredeliberationEtudiant; tous_etudiants_valides: boolean }>>(`/predeliberations/${id}/valider-etudiant`, {
				method: 'POST',
				body: payload
			})
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser le composant parent gérer les notifications
				return { success: true, data: res.data?.predeliberation_etudiant || res.data as any, message: res.message }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la validation de l\'étudiant en prédélibération',
				res,
				{ action: 'Validation étudiant', entity: 'prédélibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: res.errors }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la validation de l\'étudiant en prédélibération',
				error?.response?._data || error,
				{ action: 'Validation étudiant', entity: 'prédélibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data?.errors || error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function validerPredeliberation(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<Predeliberation>>(`/predeliberations/${id}/valider`, {
				method: 'POST'
			})
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, data: res.data, message: res.message || 'Prédélibération validée avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la validation de la prédélibération',
				res,
				{ action: 'Validation', entity: 'prédélibération' }
			)
			return { success: false, message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la validation de la prédélibération',
				error?.response?._data || error,
				{ action: 'Validation', entity: 'prédélibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function casserValidationPredeliberation(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<Predeliberation>>(`/predeliberations/${id}/casser-validation`, {
				method: 'POST'
			})
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, data: res.data, message: res.message || 'Prédélibération remise en cours avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la remise en cours de la prédélibération',
				res,
				{ action: 'Remise en cours', entity: 'prédélibération' }
			)
			return { success: false, message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la remise en cours de la prédélibération',
				error?.response?._data || error,
				{ action: 'Remise en cours', entity: 'prédélibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function changerEtatPredeliberation(id: number, etat: 'en_cours' | 'valide' | 'annule') {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<Predeliberation>>(`/predeliberations/${id}/changer-etat`, {
				method: 'POST',
				body: { etat }
			})
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, data: res.data, message: res.message || 'État modifié avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors du changement d\'état de la prédélibération',
				res,
				{ action: 'Changement d\'état', entity: 'prédélibération' }
			)
			return { success: false, message, errors: res.errors }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors du changement d\'état de la prédélibération',
				error?.response?._data || error,
				{ action: 'Changement d\'état', entity: 'prédélibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function annulerPredeliberation(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<Predeliberation>>(`/predeliberations/${id}/annuler`, {
				method: 'POST'
			})
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, data: res.data, message: res.message || 'Prédélibération annulée avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de l\'annulation de la prédélibération',
				res,
				{ action: 'Annulation', entity: 'prédélibération' }
			)
			return { success: false, message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de l\'annulation de la prédélibération',
				error?.response?._data || error,
				{ action: 'Annulation', entity: 'prédélibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function createPredeliberation(payload: { session_id: number; specialite_id: number; semestre_id: number; responsable_id: number; deliberation_id?: number; commentaire?: string }) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<Predeliberation>>('/predeliberations', {
				method: 'POST',
				body: payload
			})
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, data: res.data, message: res.message || 'Prédélibération créée avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la création de la prédélibération',
				res,
				{ action: 'Création', entity: 'prédélibération' }
			)
			return { success: false, message, errors: res.errors }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la création de la prédélibération',
				error?.response?._data || error,
				{ action: 'Création', entity: 'prédélibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data?.errors || error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function updatePredeliberation(id: number, payload: { responsable_id?: number; commentaire?: string }) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<Predeliberation>>(`/predeliberations/${id}`, {
				method: 'PATCH',
				body: payload
			})
			if (res?.success) {
				return { success: true, data: res.data, message: res.message || 'Prédélibération modifiée avec succès' }
			}
			return { success: false, message: res.message, errors: extractErrorDetails((res as any)?.errors || (res as any)?.data || res) }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la mise à jour de la prédélibération',
				error?.response?._data || error,
				{ action: 'Mise à jour', entity: 'prédélibération' }
			)
			return { success: false, message, errors: extractErrorDetails(error?.response?._data?.errors || error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	// ============================================================================
	// DÉLIBÉRATION ÉTUDIANTS
	// ============================================================================

	async function getDeliberationEtudiant(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<DeliberationEtudiant>>(`/deliberation-etudiants/${id}`)
			if (res?.success) {
				return { success: true, data: res.data }
			}
			return { success: false, message: res.message }
		} catch (error: any) {
			// Ne pas afficher de toast ici, laisser la page gérer
			const message = formatErrorMessage(
				'Erreur lors de la récupération de l\'étudiant de délibération',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'étudiant délibération' }
			)
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	async function validerDeliberationEtudiant(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<DeliberationEtudiant>>(`/deliberation-etudiants/${id}/valider`, {
				method: 'POST'
			})
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, data: res.data, message: res.message || 'Étudiant validé avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la validation de l\'étudiant en délibération',
				res,
				{ action: 'Validation étudiant', entity: 'délibération' }
			)
			return { success: false, message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la validation de l\'étudiant en délibération',
				error?.response?._data || error,
				{ action: 'Validation étudiant', entity: 'délibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function validerDeliberationEtudiantAvecPhase(payload: { deliberation_id: number; inscription_id: number; phase: 'phase_1' | 'phase_2' }) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<DeliberationEtudiant>>('/deliberation-etudiants/valider-avec-phase', {
				method: 'POST',
				body: payload
			})
			if (res?.success) {
				// Ne pas toaster ici pour éviter un toast par étudiant en validation groupée.
				// Laisser les composants appelants décider s'ils veulent afficher un message.
				return { success: true, data: res.data, message: res.message }
			}
			const message = res.message || 'Erreur lors de la validation'
			return { success: false, message, errors: res.errors }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la validation de l\'étudiant en délibération',
				error?.response?._data || error,
				{ action: 'Validation étudiant', entity: 'délibération' }
			)
			return { success: false, message, errors: extractErrorDetails(error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function validerBulkDeliberationEtudiants(payload: ValiderBulkRequest) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<{ valides: number[]; echecs: any[]; total_valides: number; total_echecs: number }>>(
				'/deliberation-etudiants/valider-bulk',
				{
					method: 'POST',
					body: payload
				}
			)
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				const msg = res.message || `${res.data.total_valides} étudiant(s) validé(s)`
				return { success: true, data: res.data, message: msg, hasFailures: res.data.total_echecs > 0 }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la validation groupée des étudiants',
				res,
				{ action: 'Validation groupée', entity: 'délibération' }
			)
			return { success: false, message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la validation groupée des étudiants',
				error?.response?._data || error,
				{ action: 'Validation groupée', entity: 'délibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function modifierNoteDeliberation(payload: ModifierNoteRequest) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<{ deliberation_etudiant: DeliberationEtudiant; modification: any }>>(
				'/deliberation-etudiants/modifier-note',
				{
					method: 'POST',
					body: payload
				}
			)

			// Succès : on laisse le composant appelant gérer les toasts
			if (res?.success) {
				return { success: true, data: res.data, message: res.message }
			}

			// Échec côté backend sans exception réseau
			const message = res?.message || 'Erreur lors de la modification de la note'
			return { success: false, message, errors: res?.errors }
		} catch (error: any) {
			// Erreur renvoyée par le proxy h3 (Nuxt) pour Laravel
			const raw = error?.response?._data || error

			// Essayer d'extraire le vrai message métier côté Laravel
			let backendMessage: string | undefined

			if (raw?.data?.data?.message) {
				backendMessage = raw.data.data.message
			} else if (raw?.data?.message) {
				backendMessage = raw.data.message
			} else if (raw?.message && raw.message !== 'Bad Request') {
				backendMessage = raw.message
			}

			const finalMessage =
				backendMessage ||
				formatErrorMessage('Erreur lors de la modification de la note', raw, {
					action: 'Modification',
					entity: 'note de délibération'
				})

			return {
				success: false,
				message: finalMessage,
				errors: backendMessage ? [{ message: backendMessage }] : extractErrorDetails(raw)
			}
		} finally {
			state.loading = false
		}
	}

	async function recalculerStatistiques(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<DeliberationEtudiant>>(`/deliberation-etudiants/${id}/recalculer-statistiques`, {
				method: 'POST'
			})
			if (res?.success) {
				// Ne pas afficher de toast ici, laisser la page gérer
				return { success: true, data: res.data, message: res.message || 'Statistiques recalculées avec succès' }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors du recalcul des statistiques',
				res,
				{ action: 'Recalcul', entity: 'statistiques' }
			)
			return { success: false, message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors du recalcul des statistiques',
				error?.response?._data || error,
				{ action: 'Recalcul', entity: 'statistiques' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	// ============================================================================
	// BULLETINS
	// ============================================================================

	async function fetchBulletins(filters?: {
		deliberation_id?: number
		inscription_id?: number
		semestre_id?: number
		numero_bulletin?: string
		page?: number
		per_page?: number | 'all'
		includeHistorique?: boolean
		anneeUniversitaire?: string | null
	}) {
		state.loading = true
		try {
			const qs = new URLSearchParams()
			if (filters?.deliberation_id) qs.append('deliberation_id', String(filters.deliberation_id))
			if (filters?.inscription_id) qs.append('inscription_id', String(filters.inscription_id))
			if (filters?.semestre_id) qs.append('semestre_id', String(filters.semestre_id))
			if (filters?.numero_bulletin) qs.append('numero_bulletin', filters.numero_bulletin)
			if (filters?.page) qs.append('page', String(filters.page))
			if (filters?.per_page) qs.append('per_page', String(filters.per_page))
			applyHistoriqueParams(qs, {
				includeHistorique: filters?.includeHistorique,
				anneeUniversitaire: filters?.anneeUniversitaire,
			})

			const res = await apiData<ApiResponse<Bulletin[] | any>>(`/bulletins?${qs.toString()}`)
			if (res?.success) {
				return { success: true, data: res.data }
			}
			return { success: false, message: res.message }
		} catch (error: any) {
			// Ne pas afficher de toast ici, laisser la page gérer
			const message = formatErrorMessage(
				'Erreur lors de la récupération des bulletins',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'bulletins' }
			)
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	async function getBulletin(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<Bulletin>>(`/bulletins/${id}`)
			if (res?.success) {
				return { success: true, data: res.data }
			}
			return { success: false, message: res.message }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la récupération du bulletin',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'bulletin' }
			)
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	async function getModificationsDeliberationEtudiant(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<any[]>>(`/deliberation-etudiants/${id}/modifications`)
			if (res?.success) {
				return { success: true, data: res.data }
			}
			return { success: false, message: res.message }
		} catch (error: any) {
			// Ne pas afficher de toast ici, laisser la page gérer
			const message = formatErrorMessage(
				'Erreur lors de la récupération des modifications',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'modifications' }
			)
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	/** Export JSON tableau de délibération (PDF généré côté frontend). */
	async function getTableauDeliberationExport(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<import('@/types/documentExport').TableauDeliberationExportPayload>>(
				`/deliberations/${id}/export/tableau-deliberation`
			)
			if (res?.success) return { success: true, data: res.data, message: res.message }
			return { success: false, message: res?.message || 'Export indisponible' }
		} catch (error: any) {
			const status = error?.response?.status ?? error?.statusCode ?? error?.status
			const message = formatErrorMessage(
				status === 403 ? 'Export indisponible (délibération non validée ou accès refusé)' : 'Erreur export tableau de délibération',
				error?.response?._data || error,
				{ action: 'Export', entity: 'tableau de délibération' }
			)
			return { success: false, message, status }
		} finally {
			state.loading = false
		}
	}

	// ============================================================================
	// SNAPSHOTS DÉLIBÉRATION (historique)
	// ============================================================================

	/** Créer un snapshot (synchrone) pour une délibération validée. */
	async function createDeliberationSnapshot(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<{ snapshot_id: number; deliberation_id: number; status: string }>>(
				`/deliberations/${id}/snapshot`,
				{ method: 'POST' }
			)
			if (res?.success) return { success: true, data: res.data, message: res.message }
			return { success: false, message: res?.message || 'Snapshot impossible', errors: (res as any)?.errors }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la création du snapshot',
				error?.response?._data || error,
				{ action: 'Création', entity: 'snapshot délibération' }
			)
			return { success: false, message, errors: extractErrorDetails(error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	/**
	 * Récupérer le snapshot (backend renvoie le payload_json directement, sans enveloppe ApiResponse).
	 * Retourne { success, data } si trouvé, { success:false } si 404/erreur.
	 */
	async function getDeliberationSnapshot(id: number) {
		state.loading = true
		try {
			const res = await apiData<any>(`/deliberations/${id}/snapshot`, { method: 'GET' } as any)
			// Si le backend renvoie directement le JSON du snapshot, on le considère comme succès.
			if (res) return { success: true, data: res }
			return { success: false, message: 'Snapshot introuvable' }
		} catch (error: any) {
			const status = error?.response?.status ?? error?.statusCode ?? error?.status
			// Le backend peut renvoyer 403 tant que le snapshot n’existe pas / n’est pas autorisé.
			// Côté UI, on traite ça comme "snapshot indisponible" (non bloquant) — surtout pendant une délib en cours.
			if (status === 404 || status === 403) {
				return { success: false, notFound: true as const, message: 'Snapshot introuvable' }
			}
			const message = formatErrorMessage(
				'Erreur lors de la récupération du snapshot',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'snapshot délibération' }
			)
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	/** Statut snapshot (diagnostic). */
	async function getDeliberationSnapshotStatus(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<any>>(`/deliberations/${id}/snapshot/status`, { method: 'GET' } as any)
			if (res?.success) return { success: true, data: res.data, message: res.message }
			return { success: false, message: res?.message || 'Statut snapshot indisponible' }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la récupération du statut snapshot',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'statut snapshot' }
			)
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	/**
	 * Régénérer un snapshot (dev only côté backend).
	 * Endpoint: POST /deliberations/{id}/snapshot/regenerate
	 */
	async function regenerateDeliberationSnapshot(id: number) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<{ snapshot_id: number; deliberation_id: number; status: string }>>(
				`/deliberations/${id}/snapshot/regenerate`,
				{ method: 'POST' } as any
			)
			if (res?.success) return { success: true, data: res.data, message: res.message }
			return { success: false, message: res?.message || 'Régénération snapshot impossible', errors: (res as any)?.errors }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la régénération du snapshot',
				error?.response?._data || error,
				{ action: 'Régénération', entity: 'snapshot délibération' }
			)
			return { success: false, message, errors: extractErrorDetails(error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function fetchDeliberationNoteModifications(filters?: { deliberation_id?: number; deliberation_etudiant_id?: number; note_id?: number; type_note?: string; date_debut?: string; date_fin?: string; page?: number; per_page?: number | 'all' }) {
		state.loading = true
		try {
			const qs = new URLSearchParams()
			if (filters?.deliberation_id) qs.append('deliberation_id', String(filters.deliberation_id))
			if (filters?.deliberation_etudiant_id) qs.append('deliberation_etudiant_id', String(filters.deliberation_etudiant_id))
			if (filters?.note_id) qs.append('note_id', String(filters.note_id))
			if (filters?.type_note) qs.append('type_note', filters.type_note)
			if (filters?.date_debut) qs.append('date_debut', filters.date_debut)
			if (filters?.date_fin) qs.append('date_fin', filters.date_fin)
			if (filters?.page) qs.append('page', String(filters.page))
			if (filters?.per_page) qs.append('per_page', String(filters.per_page))
			if (filters?.per_page === 'all') qs.append('all', 'true')

			const res = await apiData<ApiResponse<any[] | any>>(`/deliberation-note-modifications?${qs.toString()}`)
			if (res?.success) {
				return { success: true, data: res.data }
			}
			return { success: false, message: res.message }
		} catch (error: any) {
			// Ne pas afficher de toast ici, laisser la page gérer
			const message = formatErrorMessage(
				'Erreur lors de la récupération des modifications',
				error?.response?._data || error,
				{ action: 'Récupération', entity: 'modifications' }
			)
			return { success: false, message }
		} finally {
			state.loading = false
		}
	}

	async function updateDeliberation(id: number, payload: { deliberation_condition_id?: number; responsable_id?: number; commentaire?: string | null; etat?: string }) {
		state.loading = true
		try {
			const res = await apiData<ApiResponse<Deliberation>>(`/deliberations/${id}`, {
				method: 'PATCH',
				body: payload
			})
			if (res?.success) {
				// Le toast est déjà affiché par le composable
				return { success: true, data: res.data, message: res.message }
			}
			// Extraire le message proprement
			const message = formatErrorMessage(
				'Erreur lors de la mise à jour de la délibération',
				res,
				{ action: 'Mise à jour', entity: 'délibération' }
			)
			return { success: false, message, errors: res.errors }
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la mise à jour de la délibération',
				error?.response?._data || error,
				{ action: 'Mise à jour', entity: 'délibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data?.errors || error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	async function updateDeliberationConditionAssociation(id: number, deliberationConditionId: number) {
		return updateDeliberation(id, { deliberation_condition_id: deliberationConditionId })
	}

	/**
	 * État workflow délibération (1re / 2e) pour un triplet session/spécialité/semestre.
	 * Ne dépend pas du filtre responsable_id — accessible aux enseignants de la maquette.
	 */
	async function getDeliberationWorkflowStatus(
		sessionId: number,
		specialiteId: number,
		semestreId: number,
		options?: { maquetteId?: number; includeRattrapage?: boolean }
	): Promise<DeliberationWorkflowStatus | null> {
		const qs = new URLSearchParams()
		qs.append('session_id', String(sessionId))
		qs.append('specialite_id', String(specialiteId))
		qs.append('semestre_id', String(semestreId))
		if (options?.maquetteId) {
			qs.append('maquette_id', String(options.maquetteId))
		}
		if (options?.includeRattrapage) {
			qs.append('include_rattrapage', '1')
		}

		try {
			const res = await apiData<ApiResponse<DeliberationWorkflowStatus>>(
				`/deliberations/workflow-status?${qs.toString()}`
			)
			if (res?.success && res.data) {
				return res.data
			}
			return null
		} catch (error: any) {
			const status = error?.status ?? error?.statusCode ?? error?.response?.status
			if (status === 403) {
				const forbiddenError = new Error('Workflow status forbidden')
				;(forbiddenError as any).status = 403
				throw forbiddenError
			}
			console.error('Erreur lors de la récupération de l\'état workflow délibération:', error)
			return null
		}
	}

	/**
	 * Vérifier si une délibération est validée pour une session/spécialité/semestre
	 * Utile pour bloquer les modifications de notes après validation
	 */
	async function checkDeliberationValidee(sessionId: number, specialiteId: number, semestreId: number, maquetteId?: number): Promise<boolean> {
		const status = await getDeliberationWorkflowStatus(sessionId, specialiteId, semestreId, { maquetteId })
		return status?.premiere_validee ?? false
	}

	/**
	 * Vérifier si une deuxième délibération (seconde) est validée pour une session/spécialité/semestre
	 * Utile pour bloquer les modifications de notes de rattrapage après validation de la seconde délibération
	 */
	async function checkDeuxiemeDeliberationValidee(sessionId: number, specialiteId: number, semestreId: number, maquetteId?: number): Promise<boolean> {
		const status = await getDeliberationWorkflowStatus(sessionId, specialiteId, semestreId, { maquetteId })
		return status?.seconde_validee ?? false
	}

	/**
	 * Réactiver une délibération annulée (et sa prédélibération associée si annulée)
	 */
	async function reactiverDeliberation(id: number) {
		state.loading = true
		try {
			// Essayer d'abord avec la route dédiée
			try {
				const res = await apiData<ApiResponse<Deliberation>>(`/deliberations/${id}/reactiver`, {
					method: 'POST'
				})
				if (res?.success) {
					// Ne pas afficher de toast ici, laisser la page gérer
					return { success: true, data: res.data, message: res.message || 'Délibération réactivée avec succès' }
				}
				// Extraire le message proprement
				const message = formatErrorMessage(
					'Erreur lors de la réactivation de la délibération',
					res,
					{ action: 'Réactivation', entity: 'délibération' }
				)
				return { success: false, message, errors: res.errors }
			} catch {
				// Fallback : utiliser PATCH avec l'état
				const res = await apiData<ApiResponse<Deliberation>>(`/deliberations/${id}`, {
					method: 'PATCH',
					body: { etat: 'en_cours' }
				})
				if (res?.success) {
					// Ne pas afficher de toast ici, laisser la page gérer
					return { success: true, data: res.data, message: res.message || 'Délibération réactivée avec succès' }
				}
				// Extraire le message proprement
				const message = formatErrorMessage(
					'Erreur lors de la réactivation de la délibération',
					res,
					{ action: 'Réactivation', entity: 'délibération' }
				)
				return { success: false, message, errors: res.errors }
			}
		} catch (error: any) {
			const message = formatErrorMessage(
				'Erreur lors de la réactivation de la délibération',
				error?.response?._data || error,
				{ action: 'Réactivation', entity: 'délibération' }
			)
			// Ne pas afficher de toast ici, laisser la page gérer
			return { success: false, message, errors: extractErrorDetails(error?.response?._data?.errors || error?.response?._data || error) }
		} finally {
			state.loading = false
		}
	}

	return {
		loading: computed(() => state.loading),
		errors: computed(() => state.errors),
		// Conditions
		fetchDeliberationConditions,
		getDeliberationCondition,
		createDeliberationCondition,
		updateDeliberationCondition,
		deleteDeliberationCondition,
		toggleActiveDeliberationCondition,
		// Délibérations
		fetchDeliberations,
		getDeliberation,
		getDeliberationEtudiants,
		updateDeliberation,
		updateDeliberationConditionAssociation,
		reactiverDeliberation,
		createDeliberationWithPredeliberation,
		demarrerPhase1,
		terminerPhase1,
		demarrerPhase2,
		terminerPhase2,
		validerDeliberation,
		annulerDeliberation,
		getListeFormatee,
		getDeliberationStatistiques,
		getTableauDeliberationExport,
		getDeliberationEtudiantListeFormatee,
		getEtudiantsRattrapage,
		getRattrapageDeliberationOutcome,
		getRattrapageSourcePremiere,
		getNotesEliminatoires,
		creerSecondeDeliberation,
		// Prédélibérations
		fetchPredeliberations,
		getPredeliberation,
		getListeFormateePredeliberation,
		validerEtudiantPredeliberation,
		validerPredeliberation,
		casserValidationPredeliberation,
		changerEtatPredeliberation,
		annulerPredeliberation,
		createPredeliberation,
		updatePredeliberation,
		// Délibération étudiants
		getDeliberationEtudiant,
		validerDeliberationEtudiant,
		validerDeliberationEtudiantAvecPhase,
		validerBulkDeliberationEtudiants,
		modifierNoteDeliberation,
		recalculerStatistiques,
		getModificationsDeliberationEtudiant,
		// Snapshots
		createDeliberationSnapshot,
		getDeliberationSnapshot,
		getDeliberationSnapshotStatus,
		regenerateDeliberationSnapshot,
		// Modifications de notes
		fetchDeliberationNoteModifications,
		// Bulletins
		fetchBulletins,
		getBulletin,
		// Vérifications
		getDeliberationWorkflowStatus,
		checkDeliberationValidee,
		checkDeuxiemeDeliberationValidee
	}
}

